import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import Navbar from '@/components/feature/Navbar';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function BlogPage() {
  const { getPublishedPosts } = useBlogPosts();
  const posts = getPublishedPosts();

  useEffect(() => {
    document.title = 'Blog | AK Energy - Renewable Energy Insights & Guides';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Explore the AK Energy blog for renewable energy insights, solar guides, battery storage tips, EV charging advice, and Australian energy policy updates.');
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* Hero */}
        <section className="bg-gradient-to-b from-primary-50/50 to-background-50 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-background-200 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-800 transition-colors whitespace-nowrap mb-6"
            >
              <i className="ri-arrow-left-line" /> Back to Home
            </Link>
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200/50 mb-5">
                <i className="ri-article-line text-primary-500 text-sm" />
                <span className="text-xs font-semibold text-primary-700 tracking-wide uppercase whitespace-nowrap">AK Energy Insights</span>
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5 leading-tight">
                Renewable Energy Blog
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-xl">
                Expert guides, industry insights, and practical advice for Australians navigating the renewable energy transition.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        {posts.length > 0 && (
          <section className="py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
              <Link
                to={`/blog/${posts[0].slug}`}
                className="group block bg-white rounded-2xl border border-background-200 overflow-hidden hover:border-primary-200 transition-all duration-300 hover:shadow-sm"
              >
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="h-64 md:h-80 overflow-hidden">
                    <img
                      src={posts[0].coverImage}
                      alt={posts[0].title}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 md:p-10 flex flex-col justify-center">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      {posts[0].tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-xs font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                      <span className="text-xs text-foreground-400">{formatDate(posts[0].publishedAt)}</span>
                    </div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3 group-hover:text-primary-700 transition-colors leading-snug">
                      {posts[0].title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                      {posts[0].excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-primary-600 font-medium text-sm group-hover:gap-3 transition-all">
                      <span>Read Article</span>
                      <i className="ri-arrow-right-line" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </section>
        )}

        {/* Post Grid */}
        <section className="pb-16 md:pb-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">Latest Articles</h2>
              <span className="text-sm text-foreground-500">{posts.length} articles</span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {posts.slice(1).map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-xl border border-background-200 overflow-hidden hover:border-primary-200 transition-all duration-300 hover:shadow-sm flex flex-col"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={post.coverImage}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-950 mb-2 group-hover:text-primary-700 transition-colors leading-snug flex-1">
                      {post.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-background-100">
                      <span className="text-xs text-foreground-400">{formatDate(post.publishedAt)}</span>
                      <span className="text-xs font-medium text-primary-600 flex items-center gap-1 group-hover:gap-1.5 transition-all">
                        Read <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Schema.org Blog Listing structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Blog',
              name: 'AK Energy Renewable Energy Blog',
              description: 'Expert guides, industry insights, and practical advice for Australians navigating the renewable energy transition.',
              url: 'https://akenergy.com.au/blog',
              publisher: {
                '@type': 'Organization',
                name: 'AK Energy',
                logo: {
                  '@type': 'ImageObject',
                  url: 'https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/473109dd943942010dc73e384d113dfa.png',
                },
              },
              blogPost: posts.map((post) => ({
                '@type': 'BlogPosting',
                headline: post.title,
                description: post.excerpt,
                url: `https://akenergy.com.au/blog/${post.slug}`,
                datePublished: post.publishedAt,
                dateModified: post.updatedAt,
                author: {
                  '@type': 'Organization',
                  name: post.author,
                },
                image: post.coverImage,
              })),
            }),
          }}
        />
      </main>
    </div>
  );
}